/**
 * Authentication Context & Hooks
 *
 * React context and hooks for authentication.
 * Part of Batch 5d migration.
 *
 * Platform-agnostic - uses adapters and callbacks for platform-specific behavior.
 */

import { useState, useEffect, useCallback, createContext, useContext, useRef, useMemo } from 'react'
import { getSupabase, getCompanyId, clearClientCompanyIdCache, createCompanyWithOwner } from '../api'
import { storage, isWeb, getRequiredEnv } from '../adapters'
import {
  AUTH_TIMEOUT,
  COMPANY_ID_TIMEOUT,
  SESSION_TIMEOUT,
} from './constants'
import {
  isTokenError,
  isUserAuthenticated as checkIsAuthenticated,
  needsEmailVerification as checkNeedsEmailVerification,
  withTimeout,
  clearAuthStorage,
} from './authCore'

// Create authentication context with default values
const SupabaseAuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  isAuthenticated: false,
  companyId: null,
  signIn: async () => {},
  signInWithApple: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  updateProfile: async () => {},
  resetPassword: async () => {},
  resendConfirmation: async () => {},
  authReady: false,
  tokenError: false,
  isSigningUp: false,
  emailVerificationSent: false,
  needsEmailVerification: false,
  emailVerified: false,
})

// Export context for direct access
export { SupabaseAuthContext }

/**
 * Custom hook for using Supabase authentication
 * @returns {object} Auth context value
 */
export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext)
  if (!context) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
  }
  return context
}

/**
 * Logger utility - respects development mode
 */
const logger = {
  auth: (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[useSupabaseAuth]', ...args)
    }
  }
}

/**
 * Company ID cache for API helpers
 * This syncs with the API layer's cache
 */
let apiCompanyIdCache = { companyId: null, userId: null }

export const setCompanyIdCache = (companyId, userId) => {
  apiCompanyIdCache = { companyId, userId }
}

export const clearHelperCompanyIdCache = () => {
  apiCompanyIdCache = { companyId: null, userId: null }
}

export const getCompanyIdFromCache = () => apiCompanyIdCache.companyId

/**
 * Authentication provider component
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {function} props.onNavigate - Navigation callback (path) => void
 * @param {function} props.getCurrentPath - Get current route path () => string
 * @param {function} props.onSignedIn - Callback when user signs in (optional)
 * @param {function} props.onSignedOut - Callback when user signs out (optional)
 * @param {string} props.baseUrl - Base URL for password reset redirects (optional)
 */
export const SupabaseAuthProvider = ({
  children,
  onNavigate,
  getCurrentPath,
  onSignedIn,
  onSignedOut,
  baseUrl,
}) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [companyId, setCompanyId] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [authError, setAuthError] = useState(null)
  const [isSigningUp, setIsSigningUp] = useState(false)
  const [emailVerificationSent, setEmailVerificationSent] = useState(false)
  const [tokenError, setTokenError] = useState(false)
  const [emailVerified, setEmailVerified] = useState(null) // null = loading, true/false = known

  // Use refs to prevent re-initialization
  const initStartedRef = useRef(false)
  const lastSessionValidationRef = useRef(0)
  const authSubscriptionRef = useRef(null)
  const companyIdPromiseRef = useRef(null)
  const realtimeChannelRef = useRef(null)

  // Get supabase client
  const supabase = getSupabase()

  // Fetch email_verified from companies table and subscribe to realtime changes
  const fetchEmailVerified = useCallback(async (cId) => {
    try {
      const { data } = await supabase
        .from('companies')
        .select('email_verified')
        .eq('id', cId)
        .single()
      setEmailVerified(data?.email_verified ?? false)

      // Subscribe to realtime changes so UI updates when user verifies
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current)
      }
      const channel = supabase
        .channel(`email-verified-${cId}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'companies',
          filter: `id=eq.${cId}`,
        }, (payload) => {
          if (payload.new && typeof payload.new.email_verified === 'boolean') {
            setEmailVerified(payload.new.email_verified)
          }
        })
        .subscribe()
      realtimeChannelRef.current = channel
    } catch (err) {
      console.warn('[useSupabaseAuth] Failed to fetch email_verified:', err)
      setEmailVerified(false)
    }
  }, [supabase])

  // Get current path helper
  const currentPath = useMemo(() => {
    if (getCurrentPath) return getCurrentPath()
    if (isWeb() && typeof window !== 'undefined') return window.location.pathname
    return '/'
  }, [getCurrentPath])

  // Get base URL helper
  const appBaseUrl = useMemo(() => {
    if (baseUrl) return baseUrl
    if (isWeb() && typeof window !== 'undefined') return window.location.origin
    return ''
  }, [baseUrl])

  // Handle token errors by clearing corrupted auth data
  const handleTokenError = useCallback(async () => {
    console.warn('[useSupabaseAuth] Handling token error - clearing auth data')
    setTokenError(true)

    try {
      // Clear all auth-related data
      await supabase.auth.signOut()

      // Clear storage using adapter
      await clearAuthStorage()

      // Reset state
      setUser(null)
      setSession(null)
      setCompanyId(null)
      setAuthError(null)
      setTokenError(false)

      console.log('[useSupabaseAuth] Auth data cleared, user needs to re-authenticate')
    } catch (error) {
      console.error('[useSupabaseAuth] Error clearing auth data:', error)
    }
  }, [supabase])

  // Navigate helper
  const navigate = useCallback((path) => {
    if (onNavigate) {
      onNavigate(path)
    } else if (isWeb() && typeof window !== 'undefined') {
      window.location.href = path
    }
  }, [onNavigate])

  // Navigate with replace (no history entry)
  const navigateReplace = useCallback((path) => {
    if (onNavigate) {
      onNavigate(path, { replace: true })
    } else if (isWeb() && typeof window !== 'undefined') {
      window.location.replace(path)
    }
  }, [onNavigate])

  // Initialize authentication - runs only once
  useEffect(() => {
    // Prevent multiple initializations
    if (initStartedRef.current) {
      return
    }
    initStartedRef.current = true

    let mounted = true

    const initializeAuth = async () => {
      try {
        logger.auth('Starting auth initialization (once only)')

        // Get initial session with timeout
        logger.auth('Getting initial session...')

        let sessionData, sessionError
        try {
          const result = await withTimeout(
            supabase.auth.getSession(),
            SESSION_TIMEOUT,
            'Session retrieval'
          )
          sessionData = result.data
          sessionError = result.error
          logger.auth('Session retrieved:', { hasSession: !!sessionData?.session })
        } catch (timeoutError) {
          console.warn('[useSupabaseAuth] Session retrieval timed out, continuing without session')
          sessionData = null
          sessionError = timeoutError
        }

        if (sessionError) {
          console.error('[useSupabaseAuth] Session error:', sessionError)

          // Check if it's a token-related error
          if (isTokenError(sessionError)) {
            console.warn('[useSupabaseAuth] Token error detected, clearing auth data')
            await handleTokenError()
          }

          if (mounted) {
            setAuthError(sessionError)
            setLoading(false)
            setAuthReady(true)
          }
          return
        }

        const initialSession = sessionData?.session

        if (initialSession?.user) {
          logger.auth('Found initial session')
          if (mounted) {
            setSession(initialSession)
            setUser(initialSession.user)

            // Get company ID with timeout protection (no email_confirmed_at blocking)
            try {
              const resolvedCompanyId = await withTimeout(
                getCompanyId(initialSession.user),
                COMPANY_ID_TIMEOUT,
                'Company ID fetch'
              )
              if (mounted) {
                setCompanyId(resolvedCompanyId)
                // Sync to API helpers cache
                if (resolvedCompanyId) {
                  setCompanyIdCache(resolvedCompanyId, initialSession.user.id)
                  // Fetch email_verified status
                  fetchEmailVerified(resolvedCompanyId)
                }
              }
            } catch (error) {
              console.warn('[useSupabaseAuth] Company ID fetch failed or timed out:', error.message)
              if (mounted) {
                setCompanyId(null)
              }
            } finally {
              if (mounted) {
                setLoading(false)
                setAuthReady(true)
              }
            }
          }
        } else {
          logger.auth('No initial session found')
          if (mounted) {
            setLoading(false)
            setAuthReady(true)
          }
        }

        // Ensure we always set loading to false and authReady to true
        if (mounted) {
          setLoading(false)
          setAuthReady(true)
        }

        // Set up auth state change listener (only once)
        if (!authSubscriptionRef.current) {
          const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            if (!mounted) return

            logger.auth('Auth state changed:', event)

            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              if (newSession?.user) {
                setSession(newSession)
                setUser(newSession.user)

                // Handle redirect after successful authentication
                if (event === 'SIGNED_IN' && currentPath === '/') {
                  console.log('[useSupabaseAuth] User signed in, redirecting to dashboard')
                  setTimeout(() => {
                    navigateReplace('/dashboard')
                  }, 100)
                }

                // Notify callback
                if (event === 'SIGNED_IN' && onSignedIn) {
                  onSignedIn(newSession.user)
                }

                // Check if user needs company creation
                const wasPendingCompanyCreation = !!newSession.user.user_metadata?.pending_company_creation
                if (wasPendingCompanyCreation) {
                  try {
                    console.log('[useSupabaseAuth] Creating company for verified user')
                    await createCompanyWithOwner(newSession.user)
                  } catch (error) {
                    console.error('[useSupabaseAuth] Error creating company:', error)
                  }
                }

                // Auto-activate employee if they're invited/pending
                if (event === 'SIGNED_IN') {
                  try {
                    const { data: activated, error: activateError } = await supabase.rpc('activate_employee')
                    if (activateError) {
                      if (process.env.NODE_ENV === 'development') {
                        console.log('[useSupabaseAuth] Employee activation check:', activateError.message)
                      }
                    } else if (activated) {
                      console.log('[useSupabaseAuth] Employee auto-activated on sign in')
                    }
                  } catch (err) {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[useSupabaseAuth] Employee activation check failed:', err)
                    }
                  }
                }

                // Only fetch company ID if we don't have it
                if (!companyId && newSession.user.id !== user?.id && !companyIdPromiseRef.current) {
                  companyIdPromiseRef.current = withTimeout(
                    getCompanyId(newSession.user),
                    COMPANY_ID_TIMEOUT,
                    'Company ID fetch'
                  )
                  try {
                    const resolvedCompanyId = await companyIdPromiseRef.current
                    if (mounted) {
                      setCompanyId(resolvedCompanyId)
                      if (resolvedCompanyId) {
                        setCompanyIdCache(resolvedCompanyId, newSession.user.id)
                        fetchEmailVerified(resolvedCompanyId)

                        // Auto-send verification email for newly created companies
                        if (wasPendingCompanyCreation && newSession.user.email) {
                          try {
                            const supabaseUrl = getRequiredEnv('SUPABASE_URL')
                            fetch(`${supabaseUrl}/functions/v1/send-verification-email`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${newSession.access_token}`,
                              },
                              body: JSON.stringify({
                                email: newSession.user.email,
                                companyId: resolvedCompanyId,
                              }),
                            }).catch(err => console.warn('[useSupabaseAuth] Auto verification email error:', err))
                            console.log('[useSupabaseAuth] Auto-sent verification email')
                          } catch (err) {
                            console.warn('[useSupabaseAuth] Failed to auto-send verification email:', err)
                          }
                        }
                      }
                    }
                  } catch (error) {
                    console.warn('[useSupabaseAuth] Company ID fetch failed or timed out:', error)
                    if (mounted) {
                      setCompanyId(null)
                    }
                  } finally {
                    companyIdPromiseRef.current = null
                  }
                }

                if (mounted) {
                  setLoading(false)
                  setAuthReady(true)
                }
              }
            } else if (event === 'SIGNED_OUT') {
              setSession(null)
              setUser(null)
              setCompanyId(null)
              setEmailVerificationSent(false)
              companyIdPromiseRef.current = null
              setLoading(false)
              setAuthReady(true)

              // Notify callback
              if (onSignedOut) {
                onSignedOut()
              }
            }
          })

          authSubscriptionRef.current = subscription
        }

      } catch (error) {
        console.error('[useSupabaseAuth] Init error:', error)

        // Always ensure the app loads
        console.log('[useSupabaseAuth] Error occurred, but ensuring app loads anyway')
        if (mounted) {
          setAuthError(error)
          setLoading(false)
          setAuthReady(true)
        }
      }
    }

    initializeAuth()

    // Cleanup function
    return () => {
      mounted = false

      if (authSubscriptionRef.current?.unsubscribe) {
        authSubscriptionRef.current.unsubscribe()
        authSubscriptionRef.current = null
      }

      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current)
        realtimeChannelRef.current = null
      }
    }
  }, []) // Empty dependency array - runs only once

  // Sign in method
  const signIn = useCallback(async (email, password) => {
    try {
      setLoading(true)
      setIsSigningUp(false)

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      if (data.user && data.session) {
        setUser(data.user)
        setSession(data.session)
      }

      return { success: true, data }
    } catch (error) {
      console.error('[useSupabaseAuth] Sign in error:', error)
      return { success: false, error: error.message, code: error.code }
    } finally {
      setLoading(false)
    }
  }, [supabase])

  // Sign in with Apple method
  const signInWithApple = useCallback(async (idToken, userData = {}) => {
    try {
      setLoading(true)
      setIsSigningUp(false)

      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: idToken,
      })

      if (error) throw error

      // If new user with no company, update metadata so company creation flow triggers
      if (data.user) {
        const needsCompany = !data.user.user_metadata?.company_name && !data.user.user_metadata?.pending_company_creation

        if (needsCompany && userData.firstName) {
          // Update user metadata with provided details
          await supabase.auth.updateUser({
            data: {
              first_name: userData.firstName,
              last_name: userData.lastName || '',
              ...userData,
            }
          })
        }

        setUser(data.user)
        if (data.session) {
          setSession(data.session)
        }
      }

      return { success: true, data, isNewUser: !data.user?.user_metadata?.company_name }
    } catch (error) {
      console.error('[useSupabaseAuth] Apple sign in error:', error)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }, [supabase])

  // Sign up method
  const signUp = useCallback(async (email, password, userData = {}) => {
    try {
      setLoading(true)
      setIsSigningUp(true)
      setEmailVerificationSent(true)

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      })

      if (error) throw error

      if (data.user) {
        setUser(data.user)
        if (data.session) {
          setSession(data.session)
        }
      }

      return { success: true, data }
    } catch (error) {
      console.error('[useSupabaseAuth] Sign up error:', error)
      setIsSigningUp(false)
      setEmailVerificationSent(false)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }, [supabase])

  // Sign out method
  const signOut = useCallback(async () => {
    try {
      setLoading(true)
      setIsSigningUp(false)
      setEmailVerificationSent(false)

      if (user?.id) {
        clearClientCompanyIdCache(user.id)
        clearHelperCompanyIdCache()
      }

      const { error } = await supabase.auth.signOut()

      if (error) throw error

      setUser(null)
      setSession(null)
      setCompanyId(null)
      setEmailVerified(null)
      companyIdPromiseRef.current = null

      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current)
        realtimeChannelRef.current = null
      }

      return { success: true }
    } catch (error) {
      console.error('[useSupabaseAuth] Sign out error:', error)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }, [supabase, user?.id])

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    try {
      if (!user) throw new Error('No user authenticated')

      const { data, error } = await supabase.auth.updateUser({
        data: updates
      })

      if (error) throw error

      setUser(data.user)

      return { success: true, data }
    } catch (error) {
      console.error('[useSupabaseAuth] Profile update error:', error)
      return { success: false, error: error.message }
    }
  }, [supabase, user])

  // Reset password
  const resetPassword = useCallback(async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${appBaseUrl}/reset-password`
      })

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('[useSupabaseAuth] Password reset error:', error)
      return { success: false, error: error.message }
    }
  }, [supabase, appBaseUrl])

  // Send verification email via edge function
  const resendConfirmation = useCallback(async (email) => {
    try {
      if (!companyId) throw new Error('Company not found')

      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (!currentSession?.access_token) throw new Error('No active session')

      const supabaseUrl = getRequiredEnv('SUPABASE_URL')
      const response = await fetch(`${supabaseUrl}/functions/v1/send-verification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({ email, companyId }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to send verification email')
      }

      return { success: true }
    } catch (error) {
      console.error('[useSupabaseAuth] Resend verification error:', error)
      return { success: false, error: error.message }
    }
  }, [supabase, companyId])

  // Re-fetch companyId from the database and update local state.
  // Use after a flow that creates a company mid-session (e.g., Apple sign-in
  // onboarding's CompanyDetailsStep) so downstream screens see the new id
  // without waiting for the next SIGNED_IN event.
  const refreshCompanyId = useCallback(async () => {
    if (!user) return null
    try {
      clearClientCompanyIdCache()
      const id = await getCompanyId(user)
      setCompanyId(id)
      if (id) {
        setCompanyIdCache(id, user.id)
        fetchEmailVerified(id)
      }
      return id
    } catch (err) {
      console.warn('[useSupabaseAuth] refreshCompanyId failed:', err)
      return null
    }
  }, [user, fetchEmailVerified])

  // Calculate authentication state
  const isAuthenticated = useMemo(() => {
    return checkIsAuthenticated(user, session)
  }, [user, session])

  // Check if needs email verification
  const needsVerification = useMemo(() => {
    return checkNeedsEmailVerification(user)
  }, [user])

  // Context value
  const value = useMemo(() => ({
    user,
    session,
    loading,
    isAuthenticated,
    companyId,
    refreshCompanyId,
    signIn,
    signInWithApple,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    resendConfirmation,
    authReady,
    authError,
    tokenError,
    isSigningUp,
    emailVerificationSent,
    needsEmailVerification: needsVerification,
    emailVerified, // null = loading, true = verified, false = not verified
  }), [
    user,
    session,
    loading,
    isAuthenticated,
    companyId,
    refreshCompanyId,
    signIn,
    signInWithApple,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    resendConfirmation,
    authReady,
    authError,
    tokenError,
    isSigningUp,
    emailVerificationSent,
    needsVerification,
    emailVerified,
  ])

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  )
}

// Helper hooks

/**
 * Hook for checking auth status
 * @returns {object} Auth status
 */
export const useAuthStatus = () => {
  const { isAuthenticated, loading, authReady } = useSupabaseAuth()

  return {
    isAuthenticated,
    loading,
    authReady,
    isReady: authReady && !loading
  }
}

/**
 * Hook for accessing company information
 * @returns {object} Company info
 */
export const useCompany = () => {
  const { companyId, user } = useSupabaseAuth()

  return {
    companyId,
    user,
    hasCompany: !!companyId,
    isCompanyUser: !!companyId && !!user
  }
}

/**
 * Hook for accessing user profile
 * @returns {object} User profile info
 */
export const useUserProfile = () => {
  const { user, updateProfile } = useSupabaseAuth()

  const updateUserProfile = useCallback(async (updates) => {
    if (!user) return { success: false, error: 'No user authenticated' }

    return await updateProfile(updates)
  }, [user, updateProfile])

  return {
    user,
    profile: user?.user_metadata || {},
    updateProfile: updateUserProfile,
    canUpdate: !!user
  }
}

/**
 * Hook that requires authentication
 * Redirects to login if not authenticated
 *
 * @param {string} redirectTo - Path to redirect to (default: '/login')
 * @param {function} onNavigate - Optional navigation function
 * @returns {object} Auth state
 */
export const useRequireAuth = (redirectTo = '/login', onNavigate) => {
  const { user, loading } = useSupabaseAuth()

  useEffect(() => {
    if (!loading && !user) {
      if (onNavigate) {
        onNavigate(redirectTo)
      } else if (isWeb() && typeof window !== 'undefined') {
        window.location.href = redirectTo
      }
    }
  }, [user, loading, redirectTo, onNavigate])

  return { user, loading, isAuthenticated: !!user }
}

// Aliases for backward compatibility
export const useAuth = useSupabaseAuth
export const AuthProvider = SupabaseAuthProvider
